import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../store';
import { getDeactivation, putDeactivation, DeactivationResponse } from '../api/deactivation';

interface DeactivationState {
  userFields: string[]; // user-level, per project — surfaced in Reports "Deactivated"
  userTabs: string[];
  companyFields: string[]; // company-level — hides silently, never shown in Reports
  companyTabs: string[];
  loaded: boolean;
}

const initialState: DeactivationState = {
  userFields: [],
  userTabs: [],
  companyFields: [],
  companyTabs: [],
  loaded: false,
};

export const deactivationReducer = createSlice({
  name: 'deactivation',
  initialState,
  reducers: {
    setDeactivation: (state, action: PayloadAction<DeactivationResponse>) => {
      const { user, company } = action.payload || ({} as DeactivationResponse);
      state.userFields = user?.fields || [];
      state.userTabs = user?.tabs || [];
      state.companyFields = company?.fields || [];
      state.companyTabs = company?.tabs || [];
      state.loaded = true;
    },
    setUserFields: (state, action: PayloadAction<string[]>) => {
      state.userFields = action.payload;
    },
    setUserTabs: (state, action: PayloadAction<string[]>) => {
      state.userTabs = action.payload;
    },
    resetDeactivation: () => initialState,
  },
});

export const { setDeactivation, setUserFields, setUserTabs, resetDeactivation } =
  deactivationReducer.actions;

export default deactivationReducer.reducer;

// Loads both sets for the active project (called on project load).
export const fetchDeactivation = () => async (dispatch: AppDispatch) => {
  const res = await getDeactivation();
  if (res) {
    dispatch(setDeactivation(res));
  }
};

// Persists the current user-level sets. Optimistic: state is updated first,
// then sent; the response interceptor handles auth errors.
const persistUser = (fields: string[], tabs: string[]) => putDeactivation({ fields, tabs });

export const deactivateField = (key: string) => async (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const { userFields, userTabs } = getState().deactivation;
  if (userFields.includes(key)) return;
  const next = [...userFields, key];
  dispatch(setUserFields(next));
  await persistUser(next, userTabs);
};

export const reactivateField = (key: string) => async (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const { userFields, userTabs } = getState().deactivation;
  const next = userFields.filter((k) => k !== key);
  dispatch(setUserFields(next));
  await persistUser(next, userTabs);
};

export const deactivateTab = (key: string) => async (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const { userFields, userTabs } = getState().deactivation;
  if (userTabs.includes(key)) return;
  const next = [...userTabs, key];
  dispatch(setUserTabs(next));
  await persistUser(userFields, next);
};

export const reactivateTab = (key: string) => async (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const { userFields, userTabs } = getState().deactivation;
  const next = userTabs.filter((k) => k !== key);
  dispatch(setUserTabs(next));
  await persistUser(userFields, next);
};
